import type { SideBarItem } from '@/app/shell/SideBar/sideBarItem';
import { cn } from '@/shared/utils';
import { CustomTooltip } from '@/view/shared/Tooltip';
import type React from 'react';

export function NavButton({
  item,
  isActive,
  onItemClick,
  onClose,
  collapsed,
  onHoverLabel,
}: {
  readonly item: SideBarItem;
  readonly isActive: boolean;
  readonly onItemClick: (item: SideBarItem) => void;
  readonly onClose?: (() => void) | undefined;
  readonly collapsed?: boolean | undefined;
  readonly onHoverLabel?: ((label: string | null) => void) | undefined;
}): React.JSX.Element {
  const Icon = item.icon;
  const handleHoverStart = () => {
    if (!item.disabled) item.prefetch?.();
    if (collapsed) {
      onHoverLabel?.(item.disabledTip ? `${item.label}: ${item.disabledTip}` : item.label);
    }
  };
  const button = (
    <button
      type="button"
      disabled={item.disabled ?? false}
      aria-label={collapsed ? item.label : undefined}
      onMouseEnter={handleHoverStart}
      onFocus={handleHoverStart}
      onMouseLeave={collapsed ? () => onHoverLabel?.(null) : undefined}
      onBlur={collapsed ? () => onHoverLabel?.(null) : undefined}
      onClick={() => {
        if (item.disabled) return;
        onItemClick(item);
        onClose?.();
      }}
      className={cn(
        'flex w-full items-center rounded-md py-2 text-left text-body-sm font-medium transition-colors',
        collapsed ? 'justify-center px-2' : 'gap-2.5 px-2.5',
        item.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        isActive
          ? 'bg-surface-secondary text-foreground'
          : 'text-muted-foreground hover:bg-surface-secondary hover:text-foreground',
      )}
    >
      <Icon
        size={16}
        className={cn('shrink-0', isActive ? 'text-primary' : 'text-muted-foreground')}
      />
      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      {!collapsed && item.count != null && (
        <span className="shrink-0 font-mono text-2xs tracking-[0.04em] text-muted-foreground">
          {item.count}
        </span>
      )}
    </button>
  );

  if (!collapsed && item.disabled && item.disabledTip) {
    return (
      <CustomTooltip tip={item.disabledTip} placement="right">
        {button}
      </CustomTooltip>
    );
  }
  return button;
}
